import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import {
  HeaderBackground,
  Navbar,
  Spinner,
  ErrorNotification,
  SuccessNotification,
} from '../components'
import { hasValue } from '../utils/formValidation'
import {
  getPovratDokumentiService,
  createPovratDokumentService,
  updatePovratDokumentService,
  deletePovratDokumentService,
} from '../services/PovratDokument'

const emptyForm = {
  klijent_naziv: '',
  klijent_adresa: '',
  klijent_zip: '',
  klijent_grad: '',
  klijent_id: '',
}

export default function PovratDokumenti() {
  const token = useSelector(state => state.authentication.token?.access_token)

  const [dokumenti, setDokumenti] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isForbidden, setIsForbidden] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const fetchDokumenti = useCallback(async () => {
    setIsLoading(true)
    const result = await getPovratDokumentiService({ accessToken: token })
    if (result.isOk) setDokumenti(result.data)
    else if (result.status === 403) setIsForbidden(true)
    else setErrorMessage(result.message)
    setIsLoading(false)
  }, [token])

  useEffect(() => {
    if (token) fetchDokumenti()
  }, [token, fetchDokumenti])

  const resetForm = () => {
    setForm(emptyForm)
    setEditingId(null)
    setErrors({})
  }

  const handleEdit = item => {
    setEditingId(item.id)
    setForm({
      klijent_naziv: item.klijent_naziv ?? '',
      klijent_adresa: item.klijent_adresa ?? '',
      klijent_zip: item.klijent_zip ?? '',
      klijent_grad: item.klijent_grad ?? '',
      klijent_id: item.klijent_id ?? '',
    })
    setErrors({})
  }

  const handleDelete = async id => {
    setIsProcessing(true)
    setErrorMessage(null)
    setSuccessMessage(null)

    const result = await deletePovratDokumentService({
      id,
      accessToken: token,
    })
    if (result.isOk) {
      setSuccessMessage('Dokument je uspješno obrisan.')
      if (editingId === id) resetForm()
      fetchDokumenti()
    } else {
      setErrorMessage(result.message)
    }
    setIsProcessing(false)
  }

  const validate = () => {
    const newErrors = {}
    if (!hasValue(form.klijent_naziv))
      newErrors.klijent_naziv = 'Naziv klijenta ne može biti prazan.'
    setErrors(newErrors)
    return Object.values(newErrors).every(x => x == null)
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!validate()) return

    setIsProcessing(true)
    const data = {
      ...form,
      accessToken: token,
    }

    const result = editingId
      ? await updatePovratDokumentService({ ...data, id: editingId })
      : await createPovratDokumentService(data)

    if (result.isOk) {
      setSuccessMessage(
        editingId
          ? 'Dokument je uspješno izmijenjen.'
          : 'Dokument je uspješno dodan.',
      )
      resetForm()
      fetchDokumenti()
    } else {
      setErrorMessage(result.message)
    }
    setIsProcessing(false)
  }

  const renderTextField = (label, name, required = false) => (
    <div className="field" style={{ maxWidth: '400px' }}>
      <label className="label">{`${label}${required ? ' *' : ''}`}</label>
      <div className="control">
        <input
          className="input"
          type="text"
          placeholder={`Unesite ${label.toLowerCase()}...`}
          value={form[name]}
          onChange={event => setForm({ ...form, [name]: event.target.value })}
        />
      </div>
      {errors[name] && (
        <span className="registerErrorSpan">{errors[name]}</span>
      )}
    </div>
  )

  if (!token) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda - Dokumenti" />
        <div className="section">
          <div className="container">
            <p className="has-text-centered">
              Morate biti prijavljeni kako biste pristupili ovoj stranici.
            </p>
          </div>
        </div>
      </>
    )
  }

  if (isForbidden) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda - Dokumenti" />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <HeaderBackground title="Povrat proizvoda - Dokumenti" />
      <div className="section" id="povrat_dokumenti">
        <div className="container">
          <Link to="/povrat-proizvoda" className="button mb-5">
            Povrat proizvoda
          </Link>

          <form onSubmit={handleSubmit}>
            <h2 className="subtitle">
              {editingId ? 'Izmjena klijenta' : 'Dodavanje klijenta'}
            </h2>

            {renderTextField('Naziv klijenta', 'klijent_naziv', true)}
            {renderTextField('Adresa klijenta', 'klijent_adresa')}
            {renderTextField('ZIP', 'klijent_zip')}
            {renderTextField('Grad', 'klijent_grad')}
            {renderTextField('ID klijenta', 'klijent_id')}

            <div className="field">
              <button
                className="button greenButton mt-3"
                style={{ maxWidth: '400px' }}
                type="submit"
                disabled={isProcessing}>
                {editingId ? 'Sačuvaj izmjene' : 'Dodaj klijenta'}
              </button>
              {editingId && (
                <button
                  className="button ml-3 mt-3"
                  type="button"
                  style={{ maxWidth: '400px' }}
                  onClick={resetForm}
                  disabled={isProcessing}>
                  Otkaži
                </button>
              )}
            </div>
          </form>

          {errorMessage && <ErrorNotification errorMessage={errorMessage} />}
          {successMessage && (
            <SuccessNotification successMessage={successMessage} />
          )}

          <hr />

          <h2 className="subtitle">Lista klijenata</h2>

          {isLoading && (
            <Spinner
              note="Procesiranje..."
              customStyle={{ marginTop: '2rem' }}
            />
          )}

          {!isLoading && dokumenti.length === 0 && (
            <p>Trenutno nema unesenih klijenata.</p>
          )}

          {!isLoading && dokumenti.length > 0 && (
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>Klijent</th>
                  <th>Adresa</th>
                  <th>ZIP</th>
                  <th>Grad</th>
                  <th>ID</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {dokumenti.map(item => (
                  <tr key={item.id}>
                    <td>{item.klijent_naziv}</td>
                    <td>{item.klijent_adresa}</td>
                    <td>{item.klijent_zip}</td>
                    <td>{item.klijent_grad}</td>
                    <td>{item.klijent_id}</td>
                    <td>
                      <button
                        className="button is-small"
                        type="button"
                        onClick={() => handleEdit(item)}
                        disabled={isProcessing}>
                        <FontAwesomeIcon icon={faPen} />
                      </button>{' '}
                      <button
                        className="button is-small"
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        disabled={isProcessing}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}
